import React from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ProductCard = ({ product, isSelected, onSelect, onRemove, showAddButton = true }) => {
  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    })?.format(price);
  };

  const getRatingColor = (rating) => {
    if (rating >= 4.5) return 'text-green-600';
    if (rating >= 4.0) return 'text-blue-600';
    if (rating >= 3.5) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getPerformanceColor = (score) => {
    if (score >= 90) return 'bg-green-100 text-green-800';
    if (score >= 80) return 'bg-blue-100 text-blue-800';
    if (score >= 70) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  return (
    <div className={`bg-white rounded-lg border-2 transition-all duration-200 ${
      isSelected ? 'border-primary shadow-lg' : 'border-border hover:border-primary/50'
    }`}>
      {/* Header */}
      <div className="relative p-4 border-b border-border">
        {isSelected && (
          <div className="absolute top-2 right-2">
            <Button
              variant="ghost"
              size="xs"
              iconName="X"
              onClick={() => onRemove(product?.id)}
              className="text-muted-foreground hover:text-destructive"
            />
          </div>
        )}
        
        <div className="flex items-start space-x-3">
          <div className="w-16 h-16 bg-muted rounded-lg overflow-hidden flex-shrink-0">
            <Image
              src={product?.image}
              alt={product?.imageAlt}
              className="w-full h-full object-cover"
            />
          </div>
          
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-sm text-text-primary truncate">
              {product?.name}
            </h3>
            <p className="text-xs text-muted-foreground mt-1">
              {product?.brand}
            </p>
            
            {/* Rating */}
            <div className="flex items-center space-x-2 mt-2">
              <div className="flex items-center space-x-1">
                <Icon name="Star" size={14} className={getRatingColor(product?.rating)} />
                <span className={`text-sm font-medium ${getRatingColor(product?.rating)}`}>
                  {product?.rating}
                </span>
              </div>
              <span className="text-xs text-muted-foreground">
                ({product?.reviewCount} reviews)
              </span>
            </div>
          </div>
        </div>
      </div>
      {/* Price & Performance */}
      <div className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-lg font-bold text-text-primary">
              {formatPrice(product?.currentPrice)}
            </div>
            {product?.originalPrice > product?.currentPrice && (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-muted-foreground line-through">
                  {formatPrice(product?.originalPrice)}
                </span>
                <span className="text-xs bg-success text-success-foreground px-2 py-1 rounded">
                  {Math.round(((product?.originalPrice - product?.currentPrice) / product?.originalPrice) * 100)}% OFF
                </span>
              </div>
            )}
          </div>
          
          <div className="text-right">
            <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPerformanceColor(product?.performanceScore)}`}>
              {product?.performanceScore}/100
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              Performance
            </div>
          </div>
        </div>

        {/* Key Features */}
        <div className="space-y-2">
          <div className="text-xs font-medium text-text-secondary">Key Features</div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="flex items-center space-x-1">
              <Icon name="Zap" size={12} className="text-primary" />
              <span className="text-muted-foreground">{product?.power}W</span>
            </div>
            <div className="flex items-center space-x-1">
              <Icon name="Weight" size={12} className="text-primary" />
              <span className="text-muted-foreground">{product?.weight}lbs</span>
            </div>
            <div className="flex items-center space-x-1">
              <Icon name="Volume2" size={12} className="text-primary" />
              <span className="text-muted-foreground">{product?.noiseLevel}dB</span>
            </div>
            <div className="flex items-center space-x-1">
              <Icon name="Container" size={12} className="text-primary" />
              <span className="text-muted-foreground">{product?.capacity}L</span>
            </div>
          </div>
        </div>

        {/* Expert Badge */}
        {product?.expertChoice && (
          <div className="flex items-center space-x-2 p-2 bg-accent/10 rounded-lg">
            <Icon name="Award" size={14} className="text-accent" />
            <span className="text-xs font-medium text-accent-foreground">
              Expert Choice
            </span>
          </div>
        )}

        {/* Action Button */}
        {showAddButton && !isSelected && (
          <Button
            variant="outline"
            size="sm"
            iconName="Plus"
            iconPosition="left"
            onClick={() => onSelect(product)}
            fullWidth
            className="mt-3"
          >
            Add to Compare
          </Button>
        )}
      </div>
    </div>
  );
};

export default ProductCard;